import React, { useMemo, useState } from 'react';
import Table from '../common/Table';
import FormField from '../common/FormField';
import { PermissionDTO } from '../../services/apiClient';
import { Search } from 'lucide-react'; // Import the search icon

interface PermissionsTableProps {
  permissions: PermissionDTO[];
  selectedPermissions: string[];
  onPermissionChange: (permission: string, isSelected: boolean) => void;
}

interface GroupedPermissions {
  entity: string;
  category: string;
  permissions: Record<string, PermissionDTO>;
}

const splitPascalCase = (str: string): string => {
  return (
    str
      // Insert a space between lower & upper
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Space before last upper in a sequence
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      // Uppercase the first character
      .replace(/^./, (str) => str.toUpperCase())
  );
};

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  permissions,
  selectedPermissions,
  onPermissionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter permissions based on search term
  const filteredPermissions = useMemo(() => {
    if (!searchTerm) return permissions;

    return permissions.filter(
      (permission) =>
        (permission.name??'').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (permission.entity &&
          permission.entity.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (permission.category &&
          permission.category.toLowerCase().includes(searchTerm.toLowerCase())),
    );
  }, [permissions, searchTerm]);

  const availableActions = useMemo(() => {
    const actions = new Set<string>();
    filteredPermissions.forEach((permission) => {
      if (permission.action) {
        actions.add(permission.action);
      }
    });
    return Array.from(actions).sort();
  }, [filteredPermissions]);

  // Group permissions by entity and category
  const tableData = useMemo(() => {
    const grouped = filteredPermissions.reduce(
      (acc: GroupedPermissions[], permission) => {
        const entity = permission.entity || 'General';
        const category = permission.category || 'Other';

        let entityGroup = acc.find((g) => g.entity === entity);
        if (!entityGroup) {
          entityGroup = {
            entity,
            category,
            permissions: {},
          };
          acc.push(entityGroup);
        }

        entityGroup.permissions[permission.action || ''] = permission;
        return acc;
      },
      [],
    );

    return grouped;
  }, [filteredPermissions]);

  const renderCheckbox = (row: GroupedPermissions, action: string) => {
    const permission = row.permissions[action];
    if (!permission) return <></>;

    return (
      <input
        type="checkbox"
        checked={selectedPermissions.includes(permission.name || '')}
        onChange={(e) =>
          onPermissionChange(permission.name || '', e.target.checked)
        }
        className="form-checkbox h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
      />
    );
  };

  // Add this new function inside the PermissionsTable component
  const handleSelectAllForAction = (action: string, isSelected: boolean) => {
    // Get all permissions for this action
    tableData.forEach((row) => {
      const permission = row.permissions[action];
      if (permission?.name) {
        onPermissionChange(permission.name, isSelected);
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <FormField
          id="permissions-search"
          label="Search Permissions"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or category ..."
        />
      </div>

      <Table
        allowPaging={false}
        columns={[
          {
            label: '',
            key: 'entity',
            format: (value) => splitPascalCase(value),
          },
          // Dynamically create columns for each action
          ...availableActions.map((action) => ({
            label: (
              <div className="flex items-center justify-between">
                <span>{splitPascalCase(action)}</span>
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  checked={tableData.every((row) => {
                    const permission = row.permissions[action];
                    return !permission?.name || selectedPermissions.includes(permission.name);
                  })}
                  onChange={(e) => handleSelectAllForAction(action, e.target.checked)}
                />
              </div>
            ),
            key: action.toLowerCase(),
            width: '100px',
            render: (row: GroupedPermissions) => renderCheckbox(row, action),
          })),
        ]}
        data={tableData}
        groupBy={['category']}
        groupHeaderRender={(group) => (
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {splitPascalCase(group)}
            </span>
          </div>
        )}
        currentPage={1}
        totalPages={1}
        pageSize={tableData.length}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        isLoading={false}
      />

      {tableData.length === 0 && (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          No permissions match your search
        </div>
      )}
    </div>
  );
};

export default PermissionsTable;
